import { Autocomplete, TextField, Theme, Box } from '@mui/material';
import { SxProps } from '@mui/system';
import { State } from '@progress/kendo-data-query';
import * as React from 'react';
import { useTransition, useState, useRef } from 'react';
import VendorApi from 'src/api/VendorApi';
import { Vendor } from 'src/types/vendor';

type TypeAheadProps = {
  sx?: SxProps<Theme>;
  onChange: (value: NonNullable<string | Vendor>) => void;
  style?: React.CSSProperties;
};

const VendorTypeAhead: React.FC<React.PropsWithChildren<TypeAheadProps>> = ({ onChange, sx, style }) => {
  const currentSearchString = useRef<string>('');
  const [, startTransition] = useTransition();
  const [vendors, setVendors] = useState<Vendor[]>([]);

  const onInputChange = (searchText: string) => {
    currentSearchString.current = searchText;

    if (searchText == null || searchText.length < 2) {
      startTransition(() => {
        setVendors([]);
      });
      return;
    }

    VendorApi.getVendors({
      take: 20,
      filter: {
        logic: 'and',
        value: '',
        filters: [
          {
            filters: [
              { field: 'numberString', operator: 'contains', value: searchText },
              { field: 'name', operator: 'contains', value: searchText },
            ],
            logic: 'or',
          },
        ],
      },
    } as State).then((result) => {
      if (searchText === currentSearchString.current) {
        startTransition(() => {
          setVendors(result.value);
        });
      }
    });
  };

  return (
    <Autocomplete
      sx={sx}
      options={vendors}
      filterOptions={(x) => x}
      multiple={false}
      autoComplete
      style={style}
      onChange={(e, newValue) => {
        onChange(newValue);
      }}
      onInputChange={(e, newValue) => {
        onInputChange(newValue);
      }}
      isOptionEqualToValue={(option, value) => option.number === value.number}
      getOptionLabel={(option: Vendor) => `${option.name} (${option.number})`}
      renderOption={(props, option: Vendor) => {
        return (
          <Box
            component="li"
            {...props}
            id={'vendor_' + option.number}
            key={option.number}
          >
            {`${option.name} - (${option.number})`}
          </Box>
        );
      }}
      renderInput={(params) => (
        <>
          <TextField
            {...params}
            name="vendorTypeahead"
            label="Search Vendors..."
            margin="normal"
            onKeyDown={(e: any) => {
              e.stopPropagation();
            }}
          />
        </>
      )}
    />
  );
};

export default VendorTypeAhead;
